/**
 * @file order-registry-cells.ts
 * @description Что показывать в ячейках реестра заказов (phase19.31, зона
 * «Заказы», макет (а)): стадия, оплата, доставка, источник — чистыми
 * функциями, а разметку рисуют колонки (`components/orders/registry`).
 *
 * Тон стадии берём по ВИДУ стадии (SC26), а не по цвету, который задал
 * магазин: цвет магазина — только точкой (SC28). Иначе светлый цвет стадии
 * на белом не читался (находка разбора A по «Сделкам»).
 *
 * Вторая линия «Стадии» отвечает на вопрос «почему стоит»: сначала проблема
 * (`problemNow`) красным, иначе — что с согласованием доставки словами (без
 * «✓»).
 */

import type { DeliveryAgreementStatus } from "./api-delivery-agreement";
import {
  ORDER_DELIVERY_CARRIER_LABEL,
  ORDER_DELIVERY_KIND_LABEL,
  ORDER_DELIVERY_SHIPMENT_STATUS_LABEL,
  ORDER_STAGE_KIND_LABEL,
  ORDER_STATUS_LABEL,
  currencySymbol,
  formatMoney,
  type OrderDto,
  type OrderSource,
  type OrderStageKind,
  type OrderStatus,
} from "./api-orders";
import { ORDER_CHAIN_PROBLEM_LABEL } from "./order-chain-owner";
import { isOrderRefunded, resolvePaymentMethodLabel } from "./order-summary-lines";
import type { StatusTone } from "./shop/status-meta";
import { formatShopDate } from "./shop-time";
import { safeStageColor } from "./stage-color";

/** Тон пилюли по виду стадии: ждём кого-то — янтарный, отмена — красный (SC26). */
export const ORDER_STAGE_KIND_STATUS_TONE: Record<OrderStageKind, StatusTone> = {
  CREATED: "progress",
  PENDING_DELIVERY_AGREEMENT: "attention",
  AWAITING_PAYMENT: "attention",
  PAID: "progress",
  PACKAGING: "progress",
  PACKED: "progress",
  HANDED_TO_CARRIER: "progress",
  READY_FOR_PICKUP: "attention",
  IN_TRANSIT: "progress",
  ARRIVED_AT_PICKUP: "attention",
  DELIVERED: "success",
  COMPLETED: "success",
  CANCELLED: "error",
  RETURN: "attention",
};

/** Старые заказы без стадии (до phase10.5) — тон по прежнему статусу. */
const LEGACY_STATUS_TONE: Record<OrderStatus, StatusTone> = {
  NEW: "progress",
  CONFIRMED: "progress",
  AWAITING_PAYMENT: "attention",
  PAID: "progress",
  ASSEMBLING: "progress",
  PACKED: "progress",
  SHIPPED: "progress",
  DELIVERED: "success",
  COMPLETED: "success",
  CANCELLED: "error",
  RETURN_REQUESTED: "attention",
  RETURNED: "attention",
};

/** Пилюля стадии: подпись, тон и цвет точки магазина (`null` — точка тона). */
export interface OrderStageView {
  label: string;
  tone: StatusTone;
  color: string | null;
}

export function orderStageView(order: Pick<OrderDto, "stage" | "status">): OrderStageView {
  const stage = order.stage;
  if (!stage) {
    return { label: ORDER_STATUS_LABEL[order.status], tone: LEGACY_STATUS_TONE[order.status], color: null };
  }
  return {
    label: stage.name || ORDER_STAGE_KIND_LABEL[stage.kind],
    tone: ORDER_STAGE_KIND_STATUS_TONE[stage.kind],
    // Цвет магазина уходит в `style` — только проверенный hex.
    color: safeStageColor(stage.color),
  };
}

/** Согласование доставки словами — вторая линия «Стадии». */
export const ORDER_AGREEMENT_LINE: Record<DeliveryAgreementStatus, string> = {
  AWAITING_CALCULATION: "Доставка ждёт расчёта",
  AWAITING_CONFIRMATION: "Ждём подтверждения клиента",
  CONFIRMED: "Доставка согласована",
  CANCELLED: "Заявка на доставку отменена",
  OUTDATED: "Заявка на доставку устарела",
};

/** С заглавной: подписи проблем в реестре владельцев — со строчной. */
function capitalize(text: string): string {
  return text ? text[0]!.toUpperCase() + text.slice(1) : text;
}

/** Вторая линия «Стадии»: проблема (красным) или согласование доставки. */
export function orderStageNote(order: OrderDto): { text: string; problem: boolean } | null {
  if (order.problemNow) return { text: capitalize(ORDER_CHAIN_PROBLEM_LABEL[order.problemNow]), problem: true };
  const agreement = order.deliveryAgreement?.status;
  if (agreement) return { text: ORDER_AGREEMENT_LINE[agreement], problem: false };
  return null;
}

/** Короткие подписи источника для строки реестра («CRM (вручную)» не влезает). */
export const ORDER_SOURCE_SHORT: Record<OrderSource, string> = {
  CRM_MANUAL: "Вручную",
  SITE: "Сайт",
  TELEGRAM: "Telegram",
  AVITO: "Avito",
  VK: "ВКонтакте",
  PHONE: "Телефон",
};

/** Ячейка в две линии: основная, подпись и тон основной линии. */
export interface OrderCellLines {
  primary: string;
  secondary: string | null;
  tone: StatusTone | null;
}

/** «Оплата»: оплачен ли и чем; возврат средств перебивает остальное. */
export function orderPaymentCell(order: OrderDto): OrderCellLines {
  if (isOrderRefunded(order)) return { primary: "Средства возвращены", secondary: null, tone: "error" };
  const summary = order.payment;
  const status = summary
    ? summary.status
    : order.paymentStatus === "FULL" || order.paymentStatus === "PARTIAL"
      ? order.paymentStatus
      : "NONE";
  const method = resolvePaymentMethodLabel(summary?.lastMethod);
  if (status === "FULL") return { primary: "Оплачен", secondary: method, tone: "success" };
  if (status === "PARTIAL") {
    const paid = summary
      ? `${formatMoney(String(summary.paidTotal))} из ${formatMoney(order.totalGross)} ${currencySymbol(order.currency)}`
      : null;
    return { primary: "Оплачен частично", secondary: paid, tone: "attention" };
  }
  return { primary: "Не оплачен", secondary: order.paymentTerms === "POSTPAY" ? "при получении" : null, tone: null };
}

/** «Доставка»: способ и перевозчик; ниже — трек (моноширинным) или состояние отгрузки. */
export interface OrderDeliveryCell {
  primary: string;
  /** Трек-номер — единственное, что в строке набрано моноширинным (SC43). */
  track: string | null;
  status: string | null;
}

export function orderDeliveryCell(order: OrderDto): OrderDeliveryCell {
  const delivery = order.delivery;
  if (!delivery || (!delivery.kind && !delivery.carrier)) return { primary: "Не выбрана", track: null, status: null };
  if (delivery.kind === "PICKUP") return { primary: ORDER_DELIVERY_KIND_LABEL.PICKUP, track: null, status: null };
  const parts: string[] = [];
  if (delivery.carrier) parts.push(ORDER_DELIVERY_CARRIER_LABEL[delivery.carrier]);
  if (delivery.kind) parts.push(ORDER_DELIVERY_KIND_LABEL[delivery.kind]);
  return {
    primary: capitalize(parts.join(" · ")),
    track: delivery.trackNumber,
    status: delivery.trackNumber ? null : ORDER_DELIVERY_SHIPMENT_STATUS_LABEL[delivery.shipmentStatus],
  };
}

/** Деньги для строки: «1 175,00 ₽» (SC43). */
export function orderMoney(order: Pick<OrderDto, "totalGross" | "currency">): string {
  return `${formatMoney(order.totalGross)} ${currencySymbol(order.currency)}`;
}

/**
 * Дата заказа в строке реестра: «22.09» в текущем году, «22.09.2025» —
 * в прошлых (полная дата не помещалась рядом с источником в 144px).
 */
export function orderShortDate(iso: string, now: Date = new Date()): string {
  const full = formatShopDate(iso);
  return full.endsWith(`.${now.getFullYear()}`) ? full.slice(0, -5) : full;
}
